import { forwardRef, useImperativeHandle, useState } from "react";
import { UploadFileInfo } from "@progress/kendo-react-upload";
import { useTranslation } from "react-i18next";
import CustomModal from "ui/modal/Modal";
import { CustomInput } from "ui/Input";
import CustomButton from "ui/Button";
import { ICON_NAMES } from "utils/constant";
import DocumentFileUpload from "./DocumentFileUpload";

interface DocumentUploadModalProps {
	isUploading: boolean;
	canCreate: boolean;
	onUpload: (files: UploadFileInfo[], description: string) => Promise<UploadFileInfo[]>;
}

export interface DocumentUploadModalHandle {
	open: () => void;
	close: () => void;
}

const DocumentUploadModal = forwardRef<DocumentUploadModalHandle, DocumentUploadModalProps>(({
	isUploading,
	canCreate,
	onUpload,
}, ref) => {
	const { t } = useTranslation();
	// Editing the description must not re-render the parent and remount its grids.
	const [description, setDescription] = useState("");
	const [files, setFiles] = useState<UploadFileInfo[]>([]);
	const [openUploadModal, setOpenUploadModal] = useState(false);
	useImperativeHandle(ref, () => ({
		open: () => {
			if (!canCreate || isUploading) return;
			setDescription("");
			setFiles([]);
			setOpenUploadModal(true);
		},
		close: () => {
			if (!isUploading) { setOpenUploadModal(false); setFiles([]); }
		},
	}), [canCreate, isUploading]);
	if (!openUploadModal) return null;
	return (
		<CustomModal
			onClose={() => {
				if (!isUploading) setOpenUploadModal(false);
			}}
			title=""
		>
			<DocumentFileUpload
				files={files}
				setFiles={setFiles}
				disabled={isUploading}
			/>
			<div style={{ marginTop: "30px", marginBottom: "10px" }}>
				<CustomInput
					multiple
					value={description}
					disabled={isUploading}
					onChange={setDescription}
					label={t("Description")}
					placeholder={t("Description")}
				/>
				<div style={{ marginTop: "20px" }}>
					<CustomButton
						iconName={ICON_NAMES.Upload}
						onClick={async () => {
							const failedFiles = await onUpload([...files], description);
							setFiles(failedFiles);
						}}
						loading={isUploading}
						disabled={isUploading || files.length === 0 || !canCreate}
						label={t("Upload")}
						iconRight={false}
					/>
				</div>
			</div>
		</CustomModal>
	);
});

export default DocumentUploadModal;
